import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect, useState } from 'react';

import { getCreatorApiUrl } from '@client/components/api-urls';
import auth from '@client/components/auth';
import { formatISODate } from '@client/helpers/date-utils';
import { Async } from '@client/new/helpers/types';
import { FilterOption } from '@design-system/components/dropDown/dropDownFilter';
import { BusinessModel } from '@playbook-technologies/common-enums';

import { usePaymentMethod } from './usePaymentMethod';

type useSubscriptionPayoutsProps = {
  date: Date;
  creatorId: number;
};

export type EarningsBreakdown = {
  date: string;
  businessModel: BusinessModel;
  limitedMonthlyRevenue: number;
  monthlyRevenue: number;
  annualRevenue: number;
  limitedAnnualRevenue: number;
  futureAnnualRevenue: number;
  futureRefundsAndDisputesRevenue: number;
  totalFutureRevenue: number;
  quarterlyRevenue: number;
  engagementRevenue: number;
  refundsAndDisputesRevenue: number;
  subsidy: number;
  totalSubscriptionRevenue: number;
  payoutFee: number;
  totalPayout: number;
  paidAt?: string;
  hasPrograms: boolean;
  bundles: BundleEarningsBreakdown[];
  paymentMethod?: number;
};

export enum BillingCycleInterval {
  Month = 'month',
  Year = 'year',
}

export type EarningsByProduct = {
  productId: string;
  price: number;
  billingCycleInterval: BillingCycleInterval;
  monthlyRevenue: number;
  annualRevenue: number;
  quarterlyRevenue: number;
  engagementRevenue: number;
  refundsAndDisputesRevenue: number;
  futureAnnualRevenue: number;
  futureRefundsAndDisputesRevenue: number;
  totalFutureRevenue: number;
  totalSubscriptionRevenue: number;
};

export type BundleEarningsBreakdown = {
  id: number;
  title: string;
  earningsByProduct: EarningsByProduct[];
  monthlyRevenue: number;
  annualRevenue: number;
  quarterlyRevenue: number;
  engagementRevenue: number;
  refundsAndDisputesRevenue: number;
  futureAnnualRevenue: number;
  futureRefundsAndDisputesRevenue: number;
  totalFutureRevenue: number;
  totalSubscriptionRevenue: number;
};

export type SubscriptionPayouts = EarningsBreakdown & {
  productFilter: {
    options: Array<FilterOption<string>>;
    label: string;
    selectedValue?: string;
    deleteSelection: () => void;
    onSelect: (value?: string) => void;
    show: boolean;
  };
  payoutFeeLabel: string;
};

const fetchPayoutData = async (
  creatorId: number,
  date: Date,
): Promise<EarningsBreakdown> => {
  const url = new URL(getCreatorApiUrl(`/payout/${creatorId}/dashboard`));

  const response = await axios.get<EarningsBreakdown>(url.toString(), {
    params: {
      date: formatISODate(date),
    },
    headers: {
      Authorization: auth.getToken(),
    },
  });

  return response.data;
};

const mapBundlesToProductOptions = (bundles: BundleEarningsBreakdown[]) => {
  const productOptions = bundles.flatMap((bundle) =>
    bundle.earningsByProduct.map((product) => {
      const cycle =
        product.billingCycleInterval === BillingCycleInterval.Month
          ? 'Monthly'
          : 'Annual';
      return {
        label: `${bundle.title} - ${cycle} - $${(product.price / 100).toFixed(
          2,
        )}`,
        value: product.productId,
      };
    }),
  );

  const productsWithRevenue = bundles
    .flatMap((bundle) => bundle.earningsByProduct)
    .filter((ebp) => ebp.totalSubscriptionRevenue > 0);

  return productOptions.filter((option) =>
    productsWithRevenue.some((ebp) => ebp.productId === option.value),
  );
};

export const useSubscriptionPayouts = ({
  date,
  creatorId,
}: useSubscriptionPayoutsProps): Async<SubscriptionPayouts> => {
  const { isFetched, data, isError } = useQuery(
    ['payout-dashboard', creatorId, date],
    () => fetchPayoutData(creatorId, date),
  );

  const paymentMethod = usePaymentMethod(data?.paymentMethod);

  const [selectedProduct, setProductFilter] = useState<string | undefined>(
    undefined,
  );
  useEffect(() => setProductFilter(undefined), [creatorId, date]);

  if (isError) {
    return {
      type: 'fetch-error',
      value: 'could not fetch data',
    };
  }

  if (isFetched && data) {
    const productOptions = mapBundlesToProductOptions(data.bundles);

    const selectedBundle = selectedProduct
      ? data.bundles.find((bundle) =>
          bundle.earningsByProduct.some(
            (ebp) => ebp.productId === selectedProduct,
          ),
        )
      : null;

    return {
      type: 'fetched',
      value: {
        ...data,
        // if a product is selected, show only its bundle data by elevating selected bundle to top-level
        ...(selectedBundle || {}),
        bundles: selectedBundle ? [selectedBundle] : data.bundles,
        productFilter: {
          options: productOptions,
          label: 'Price',
          selectedValue: selectedProduct,
          onSelect: (value) => setProductFilter(value),
          deleteSelection: () => setProductFilter(undefined),
          show: productOptions.length > 1,
        },
        payoutFeeLabel: `${paymentMethod.name} Transfer Fee`,
      },
    };
  }

  return {
    type: 'fetching',
  };
};
